import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowRight, Loader2, Sparkles, Upload } from 'lucide-react';
import { resumeAnalysisAPI } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import ResumeAnalysisBlurredPreview, {
  ResumePreviewAnalysis,
} from '@/components/profile/ResumeAnalysisBlurredPreview';

/** Use `/#resume-ai-analysis` in carousel button links to scroll here from the home page hero. */
export const RESUME_AI_HOME_SECTION_ID = 'resume-ai-analysis';

const ResumeAnalysisTeaser: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState('');
  const [fileName, setFileName] = useState('');
  const [preview, setPreview] = useState<ResumePreviewAnalysis | null>(null);

  const isAspirant = isAuthenticated && user?.role === 'aspirant';
  const profileLink = '/aspirant/profile?tab=resume-analysis';

  const handleFile = async (file: File) => {
    setError('');
    setPreview(null);
    setFileName(file.name);
    setAnalyzing(true);
    try {
      const response = await resumeAnalysisAPI.preview(file);
      if (response.success) {
        setPreview(response.data.analysis);
      }
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Could not analyze resume. Try a PDF or DOCX file.';
      setError(message);
    } finally {
      setAnalyzing(false);
    }
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  if (isAspirant) {
    return (
      <section
        id={RESUME_AI_HOME_SECTION_ID}
        className="py-16 bg-gradient-to-br from-orange-50/60 to-white border-y border-orange-100 scroll-mt-24"
      >
        <div className="max-w-4xl mx-auto px-6 text-center space-y-6">
          <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-800 px-4 py-1.5 rounded-full text-sm font-medium">
            <Sparkles className="w-4 h-4" />
            Resume AI Insights
          </div>
          <h2 className="text-3xl font-bold text-secondary">Your resume analysis is ready</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            View your ATS score, improvement tips, and skill recommendations on your profile.
          </p>
          <Link to={profileLink}>
            <Button className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 rounded-xl">
              Open Resume Analysis
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section
      id={RESUME_AI_HOME_SECTION_ID}
      className="py-16 bg-gradient-to-br from-orange-50/60 to-white border-y border-orange-100 scroll-mt-24"
    >
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-center space-y-4 mb-10">
          <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-800 px-4 py-1.5 rounded-full text-sm font-medium">
            <Sparkles className="w-4 h-4" />
            Free AI Resume Check
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-secondary">
            How ATS-friendly is your resume?
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Upload your resume for an instant ATS score and improvement preview.
            Create a free account to unlock the full report on your profile.
          </p>
        </div>

        <Card className="border-orange-200 shadow-lg overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-orange-50 to-white border-b border-orange-100">
            <CardTitle className="flex items-center gap-2 text-xl">
              <Sparkles className="w-5 h-5 text-orange-500" />
              Try Resume AI Analysis
            </CardTitle>
            <CardDescription>PDF or DOCX · Preview is free · No account required</CardDescription>
          </CardHeader>
          <CardContent className="p-6 sm:p-8 space-y-6">
            {!preview && !analyzing && (
              <div
                className="border-2 border-dashed border-orange-200 rounded-xl p-10 text-center cursor-pointer hover:border-orange-400 hover:bg-orange-50/30 transition-colors"
                onClick={() => fileInputRef.current?.click()}
                onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
                role="button"
                tabIndex={0}
              >
                <Upload className="w-10 h-10 text-orange-400 mx-auto mb-3" />
                <p className="font-medium text-slate-800">Drop your resume here or click to upload</p>
                <p className="text-sm text-slate-500 mt-1">PDF or DOCX, up to 10 MB</p>
              </div>
            )}

            {analyzing && (
              <div className="flex flex-col items-center py-12 gap-3">
                <Loader2 className="w-10 h-10 text-orange-500 animate-spin" />
                <p className="text-slate-600">Analyzing {fileName || 'your resume'}…</p>
              </div>
            )}

            {error && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-4">
                {error}
              </div>
            )}

            {preview && !analyzing && (
              <div className="space-y-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-sm text-slate-600">
                    Preview for <span className="font-medium text-slate-900">{fileName}</span>
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setPreview(null);
                      setFileName('');
                      if (fileInputRef.current) fileInputRef.current.value = '';
                    }}
                  >
                    Try another file
                  </Button>
                </div>

                <ResumeAnalysisBlurredPreview
                  analysis={preview}
                  fileName={fileName}
                  overlayMessage="Sign up to unlock full analysis"
                  subtitle="Create a free account to see every improvement tip, skill gap, and certification suggestion."
                />

                <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
                  <Link to="/register">
                    <Button className="w-full sm:w-auto bg-orange-500 hover:bg-orange-600 text-white px-8">
                      Sign up for full analysis
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                  <Link to="/login">
                    <Button variant="outline" className="w-full sm:w-auto">
                      Already have an account? Sign in
                    </Button>
                  </Link>
                </div>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              className="hidden"
              onChange={onInputChange}
            />
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default ResumeAnalysisTeaser;
